import {
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from "posthog-js/react";

import { POSTHOG_KEY } from "./env";
import { posthogClient } from "./posthog";

/**
 * PostHog feature flag registry — the single source of truth for every flag
 * the frontend reads.
 *
 * Why this file exists: PR #3193 (Danone AskPuntt outage). The flag was
 * created as a Boolean in the PostHog UI so `getFeatureFlag()` returned
 * `true`, but the call site compared against the string `"enabled"` and
 * silently blocked every user. Every flag read now goes through the typed
 * accessors below, which normalize PostHog's polymorphic
 * `boolean | string | undefined` return so boolean-vs-multivariate mismatches
 * cannot break prod again.
 *
 * Adding a flag:
 *   1. Add an entry to FLAGS below with `kind: "boolean" | "variant" | "payload"`.
 *   2. Read it via `isFlagOn` / `useIsFlagOn` (boolean), `flagVariant` /
 *      `useFlagVariant` (variant), or `flagPayload` / `useFlagPayload` (payload).
 *   3. Never import `posthog-js` feature-flag APIs directly — ESLint enforces this.
 */

type BooleanFlagDef = {
  kind: "boolean";
  description?: string;
};

type VariantFlagDef<V extends string = string> = {
  kind: "variant";
  variants: readonly V[];
  description?: string;
};

type PayloadFlagDef<P = unknown> = {
  kind: "payload";
  description?: string;
  _payload?: P;
};

export const FLAGS = {
  "danone-beta-testers": {
    kind: "boolean",
    description: "Grants Danone users early access to AskPuntt",
  },
  "ask-puntt-v2": {
    kind: "boolean",
    description:
      "In-place Ask Puntt V2. After login on app.puntt.ai, flagged users reload onto the V2 tree on the same origin (same localStorage session — no second login). Create as Boolean in PostHog.",
  },
  "puntt-access": { kind: "boolean" },
  "ask-puntt-warhol": {
    kind: "boolean",
    description:
      "Route NEW Ask Puntt chats to the warhol agent instead of rembrandt. Existing chats keep the agent they were created with. Create as Boolean in PostHog.",
  },
  "ask-puntt-version-compare": {
    kind: "boolean",
    description:
      "Spike: a Compare control in the Ask Puntt V2 review workspace that shows two reviews' artworks side by side, each with its own pins, moving as one. Create as Boolean in PostHog.",
  },
  "ask-v2-pin-groups": {
    kind: "boolean",
    description:
      "Reduce noise: the Ask Puntt V2 review groups its pins by block of the pack (or place), each group with a one-line summary, and a switch hides minor and check findings (warhol spec dave.savostyanov/0090). Create as Boolean in PostHog.",
  },
  "ask-v2-review-status-tabs": {
    kind: "boolean",
    description:
      "The Ask Puntt V2 review comment list opens on To decide (the unresolved comments, high risk first) beside All, with a Group by menu (No grouping or Status), one Filter menu (Status, types, Show minor, Reset, a count) and an every-comment-has-a-status empty state (Figma November 2026, frame 0b). Create as Boolean in PostHog.",
  },
  "ask-v2-review-bulk-select": {
    kind: "boolean",
    description:
      "The Ask Puntt V2 review comment list, under ask-v2-review-status-tabs, gets a box on each comment it can judge, a Select all box beside the tabs and a bar under the list that marks every ticked comment Relevant or Not relevant (Figma November 2026, frame 0b, Bulk selection). Create as Boolean in PostHog.",
  },
  "ask-v2-review-comment-drawer": {
    kind: "boolean",
    description:
      "The Ask Puntt V2 review opens a picked comment in a drawer that takes the place of the list, with All comments to go back and N of M with previous and next (Figma November 2026, frame 0c). Create as Boolean in PostHog.",
  },
  "ask-v2-review-send-to-puntt": {
    kind: "boolean",
    description:
      "The Ask Puntt V2 review's new-comment box offers Send to Puntt: the saved comment also goes to the chat with its pin attached, so Puntt answers it there. Create as Boolean in PostHog.",
  },
  "dismiss-comments": { kind: "boolean" },
  "resolve-comments": { kind: "boolean" },
  "delete-comments": { kind: "boolean" },
  "ai-thumbs-down": { kind: "boolean" },
  "create-revisions": { kind: "boolean" },
  "show-dismissed-comments": { kind: "boolean" },
  "manage-brand-guidelines": { kind: "boolean" },
  "manage-users": { kind: "boolean" },
  "restricted-comment-edit": { kind: "boolean" },
  "comment-personas": { kind: "boolean" },
  "puntt-dashboard": { kind: "boolean" },
  "run-ai-review": {
    kind: "variant",
    variants: ["always", "first", "none"] as const,
    description: "Controls which users can trigger AI reviews",
  },
  "knowledge-database": {
    kind: "variant",
    variants: [
      "view",
      "disabled",
      "manage",
      // Legacy tiers — map to `manage` in canManageKnowledgeDatabase so
      // existing PostHog rollouts keep working without a flag-day.
      "mutate-records",
      "mutate-models",
    ] as const,
    description:
      "Tiered access to the knowledge database (view → manage). `manage` gates uploads, scope edits, promotions, and deletes; legacy `mutate-records`/`mutate-models` map to manage. PostHog variant `view` is the read tier — prior code used `enabled` which never matched in PostHog.",
  },
  "spreadsheet-extraction": {
    kind: "variant",
    variants: ["disabled", "shadow", "full"] as const,
    description:
      "Gates structured spreadsheet extraction across samba/rembrandt/carrie. `disabled` = legacy image pipeline. `shadow` = new pipeline runs in parallel, diff logged, no customer-visible change. `full` = new pipeline serves the SpreadsheetView. See improvements/2026-05-12_spreadsheet-structured-extraction.md §8.",
  },
  "persona-nicknames": {
    kind: "payload",
  } as PayloadFlagDef<Record<string, string>>,
  "ai-rules-available": {
    kind: "payload",
  } as PayloadFlagDef<Record<string, string>>,
} as const satisfies Record<
  string,
  BooleanFlagDef | VariantFlagDef | PayloadFlagDef
>;

export type FeatureFlagName = keyof typeof FLAGS;

type BooleanFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "boolean"
    ? K
    : never;
}[FeatureFlagName];

type VariantFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "variant"
    ? K
    : never;
}[FeatureFlagName];

type PayloadFlagName = {
  [K in FeatureFlagName]: (typeof FLAGS)[K]["kind"] extends "payload"
    ? K
    : never;
}[FeatureFlagName];

type VariantOf<N extends VariantFlagName> = (typeof FLAGS)[N] extends {
  variants: readonly (infer V)[];
}
  ? V
  : never;

type PayloadOf<N extends PayloadFlagName> =
  (typeof FLAGS)[N] extends PayloadFlagDef<infer P> ? P : unknown;

type RawFlagValue = boolean | string | undefined | null;

// Accept `true` (boolean flag), `"enabled"` and `"true"` (multivariate flag
// where someone named the on-variant `"enabled"` or used PostHog's default
// multivariate-from-boolean migration).
function normalizeBoolean(value: RawFlagValue): boolean {
  if (value === true) return true;
  if (typeof value === "string") {
    return value === "enabled" || value === "true";
  }
  return false;
}

function asVariantKey<N extends VariantFlagName>(
  value: RawFlagValue,
): VariantOf<N> | undefined {
  return typeof value === "string" ? (value as VariantOf<N>) : undefined;
}

// Flags that default ON in local dev builds so demos are visible with no console
// step (PostHog is not connected locally). A `ff:<name>` localStorage key still
// overrides (set "0" to force off). Prod ignores all of this.
const DEV_DEFAULT_ON: FeatureFlagName[] = [
  "ask-puntt-v2",
  "ask-puntt-version-compare",
  "ask-v2-pin-groups",
];

// Flags that are ON for every viewer of an epic (per-branch) build, so a spike
// can be shown on its branch's environment without a PostHog flag existing
// yet. `vite build --mode epic` is the only build this reaches: dev, staging
// and prod read PostHog as before.
const EPIC_DEFAULT_ON: FeatureFlagName[] = ["ask-puntt-version-compare"];

/**
 * The flag an epic (per-branch) build turns on for every viewer, or `undefined`.
 * Not a dev override: it ships in that build, and it is checked first, so a
 * `ff:<name>` key cannot turn it off there.
 */
function epicFlagDefault(name: FeatureFlagName): true | undefined {
  return import.meta.env.MODE === "epic" && EPIC_DEFAULT_ON.includes(name)
    ? true
    : undefined;
}

/**
 * DEV-ONLY local override for boolean flags. On a local build PostHog is not
 * connected (no `VITE_POSTHOG_KEY`), so every flag reads off. This lets you flip
 * one on for local testing without PostHog:
 *
 *   localStorage.setItem("ff:puntt-dashboard", "1"); // then reload
 *   localStorage.removeItem("ff:puntt-dashboard");   // turn off
 *
 * Returns `undefined` (defer to PostHog) unless an override key exists. Guarded
 * by `import.meta.env.DEV`, so prod builds tree-shake this to a constant `false`
 * branch — production flag reads are unchanged.
 */
function devFlagOverride(name: FeatureFlagName): boolean | undefined {
  if (!import.meta.env.DEV || typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(`ff:${name}`);
    if (raw != null) return raw === "1" || raw === "true";
  } catch {
    // localStorage disabled — fall through to the dev default.
  }
  return DEV_DEFAULT_ON.includes(name) ? true : undefined;
}

/**
 * Read a boolean flag synchronously (route loaders, redux thunks, imperative
 * code). Prefer `useIsFlagOn` inside React components.
 */
export function isFlagOn(name: BooleanFlagName): boolean {
  const override = epicFlagDefault(name) ?? devFlagOverride(name);
  if (override !== undefined) return override;
  return normalizeBoolean(posthogClient.getFeatureFlag(name) as RawFlagValue);
}

/**
 * Read a multivariate flag synchronously. Returns one of the declared
 * variants, or `undefined` when the flag has no assignment yet.
 */
export function flagVariant<N extends VariantFlagName>(
  name: N,
): VariantOf<N> | undefined {
  return asVariantKey<N>(posthogClient.getFeatureFlag(name) as RawFlagValue);
}

/**
 * Bound for route guards. Same budget as the in-place Ask Puntt flag wait
 * (`FLAG_WAIT_MS` in ./askV2InPlace.ts). Held as its own constant because
 * importing that one here would close an import cycle — askV2InPlace.ts
 * already imports `isFlagOn` from this module.
 */
export const FEATURE_FLAG_WAIT_MS = 4000;

let featureFlagsReady: Promise<void> | null = null;
// Whether the memoized wait settled while PostHog positively reported its
// flags loaded. Only such a wait can go stale — see `waitForFeatureFlags`.
let settledWithFlagsLoaded = false;

export function resetFeatureFlagWaitForTests(): void {
  featureFlagsReady = null;
  settledWithFlagsLoaded = false;
}

/**
 * `true` only when PostHog positively reports its flags are loaded. A client
 * that never loads them (key-less build) or does not expose the bit reads
 * `false`, which leaves the memo exactly as it was.
 */
function posthogFlagsLoaded(): boolean {
  return posthogClient?.featureFlags?.hasLoadedFlags === true;
}

function markWaitSettled(): void {
  settledWithFlagsLoaded = posthogFlagsLoaded();
}

/**
 * Resolves once PostHog has evaluated flags, or after FEATURE_FLAG_WAIT_MS.
 *
 * why: the in-flight promise is memoized here, before `loadFeatureFlags` can
 * subscribe. Caching only once the wait had settled let two callers that
 * overlap before PostHog calls back each subscribe and each arm their own
 * FEATURE_FLAG_WAIT_MS timer, which is not "one wait per page load". Same
 * shape as `waitForAskV2Flag` (./askV2InPlace.ts).
 *
 * why the staleness check: the memo has to track "flags are loaded", not "the
 * module was imported". `posthogClient.reset()` clears PostHog's flags in
 * place, and the `?t=` applyToken path (`utils/slices/auth.ts`) reaches it
 * without a document reload, so the module never re-evaluates. A settled memo
 * would then hand the route guard an instantly-resolved promise over empty
 * flags and the child guard would bounce the user to tickets — the exact race
 * this wait exists to remove. Re-arm instead, so the next guard waits out the
 * fresh `/flags` round-trip. An in-flight wait is never stale
 * (`settledWithFlagsLoaded` is false until it settles), so overlapping callers
 * still share one subscription.
 */
export function waitForFeatureFlags(): Promise<void> {
  const stale = settledWithFlagsLoaded && !posthogFlagsLoaded();
  if (featureFlagsReady && !stale) return featureFlagsReady;
  settledWithFlagsLoaded = false;
  featureFlagsReady = loadFeatureFlags();
  return featureFlagsReady;
}

function loadFeatureFlags(): Promise<void> {
  // why: a key-less build (local dev / preview with no VITE_POSTHOG_KEY) still
  // gets a real client back from `posthog.init("")`, so the `typeof subscribe`
  // guard below does NOT fire — PostHog never calls back and the first
  // authenticated navigation burns the whole FEATURE_FLAG_WAIT_MS before any
  // child route guard runs, on every fresh page load. Flags all read off
  // there anyway (see the dev-override doc above), so resolve now. Mirrors the
  // `!POSTHOG_KEY` short-circuit in `loadAskV2Flag` (./askV2InPlace.ts). That
  // one also short-circuits on a `ff:ask-puntt-v2` dev override; the guard is
  // per-flag and does not transfer to this global wait — and a dev build has no
  // key, so an override already lands on the branch below.
  if (!POSTHOG_KEY) {
    markWaitSettled();
    return Promise.resolve();
  }

  // why: posthog-js defines onFeatureFlags on the prototype and it reads
  // `this.featureFlags` — a detached reference throws, the catch below swallows
  // it, and the wait silently becomes a no-op. Keep the receiver.
  const subscribe = posthogClient?.onFeatureFlags?.bind(posthogClient);
  if (typeof subscribe !== "function") {
    markWaitSettled();
    return Promise.resolve();
  }

  let resolveReady: () => void = () => undefined;
  const pending = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  let settled = false;
  let unsubscribe: (() => void) | undefined;
  let timer: number | undefined = undefined;

  const finish = () => {
    if (settled) return;
    settled = true;
    unsubscribe?.();
    if (timer != null && typeof window !== "undefined") {
      window.clearTimeout(timer);
    }
    // why: one wait per flags-loaded epoch — `waitForFeatureFlags` already
    // cached `pending`, so a later navigation reads whatever PostHog has now
    // and only re-arms if PostHog's flags were cleared in the meantime.
    markWaitSettled();
    resolveReady();
  };

  try {
    const maybeUnsub = subscribe(() => finish());
    if (typeof maybeUnsub === "function") unsubscribe = maybeUnsub;
  } catch {
    finish();
    return pending;
  }

  if (settled) {
    unsubscribe?.();
    return pending;
  }

  if (typeof window === "undefined") {
    finish();
    return pending;
  }
  timer = window.setTimeout(() => finish(), FEATURE_FLAG_WAIT_MS);
  return pending;
}

/** Read a payload flag synchronously. */
export function flagPayload<N extends PayloadFlagName>(
  name: N,
): PayloadOf<N> | undefined {
  return posthogClient.getFeatureFlagPayload(name) as PayloadOf<N> | undefined;
}

// React hooks — re-render when PostHog pushes a flag update.
export function useIsFlagOn(name: BooleanFlagName): boolean {
  // Call the hook unconditionally (rules of hooks); the dev override wins.
  const value = normalizeBoolean(useFeatureFlagVariantKey(name));
  const override = epicFlagDefault(name) ?? devFlagOverride(name);
  return override !== undefined ? override : value;
}

export function useFlagVariant<N extends VariantFlagName>(
  name: N,
): VariantOf<N> | undefined {
  return asVariantKey<N>(useFeatureFlagVariantKey(name));
}

export function useFlagPayload<N extends PayloadFlagName>(
  name: N,
): PayloadOf<N> | undefined {
  return useFeatureFlagPayload(name) as PayloadOf<N> | undefined;
}
